// Delegation ↔ Leave bridge (web): ONE workflow across two pages.
//
// Both directions hand off through router state + a sessionStorage draft so the
// employee never loses what they typed:
//   Delegation → "Continue to Leave Request"  : prefill dates + delegation id
//   Leave → "Set up delegation"               : keep the leave draft, prefill
//                                               the delegation window from it,
//                                               come back with the new id.
// No business rules live here — the policy (required? confirmed first? CC?)
// is evaluated by the backend; this only carries the user's context.
const DRAFT_KEY = 'kp:leave-draft';

export interface LeaveDraft {
  type?: string;
  startDate?: string;
  endDate?: string;
  reason?: string;
  notes?: string;
  dayType?: string;
  delegationId?: string | null;
  delegateName?: string | null;
  savedAt?: string;
}

export interface LeavePrefill {
  startDate?: string;
  endDate?: string;
  reason?: string;
  delegationId?: string | null;
  delegateName?: string | null;
}

/** Context carried from the leave form into the delegation form. */
export interface FromLeaveContext {
  startDate?: string;
  endDate?: string;
  reason?: string;
  leaveRequestId?: string | null;
}

export function saveLeaveDraft(d: LeaveDraft): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...d, savedAt: new Date().toISOString() }));
  } catch {
    /* private mode / quota — the user simply re-enters */
  }
}

export function takeLeaveDraft(): LeaveDraft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(DRAFT_KEY);
    const d = JSON.parse(raw) as LeaveDraft;
    // A draft older than a day is stale noise, not intent.
    if (d.savedAt && Date.now() - new Date(d.savedAt).getTime() > 86_400_000) return null;
    return d;
  } catch {
    return null;
  }
}

/** Tenant-scoped in-app route: `/t/{slug}/app/<rel>` derived from any in-app pathname. */
export function appRoute(pathname: string, rel: string): string {
  const base = pathname.replace(/\/app(\/.*)?$/, '/app');
  return `${base}/${rel.replace(/^\/+/, '')}`;
}

/** ISO date (YYYY-MM-DD) → start-of-day / end-of-day local datetimes for a
 *  delegation window, applying the tenant's buffer days. */
export function delegationWindowFromLeave(
  startDate: string,
  endDate: string,
  buffers: { startDaysBefore?: number; endDaysAfter?: number } = {},
): { startsAt: string; endsAt: string } {
  const s = new Date(`${startDate}T08:00:00`);
  const e = new Date(`${endDate || startDate}T18:00:00`);
  s.setDate(s.getDate() - (buffers.startDaysBefore || 0));
  e.setDate(e.getDate() + (buffers.endDaysAfter || 0));
  const local = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  return { startsAt: local(s), endsAt: local(e) };
}
